import { computed, defineComponent, onMounted, ref, watch } from "vue";
import store from "@/store";
import { getAuth0Client } from "@/auth0";

import { viewPermissions } from "@/composables/viewPermissions";
import { appOwnerImgSrcUrl } from "@/extensions/extra";
import { getAuthSessionManager } from "@/utils/authSessionManager";
import { notifyError } from "@/utils/notifier.js";

const MANUAL_LOGOUT_FAILURE_MESSAGE = "We could not log you out. Please try again.";

export default defineComponent({
  name: "HeaderComponent",

  setup() {
    const { viewRbac } = viewPermissions([
      "accountsettings-read",
      "accountsettings-all",
    ]);

    const brandImage = ref("/img/DeCS-Color@5x-1.png");
    const userEmail = computed(() => store.state.profileData.userEmail);
    const userName = computed(() => store.state.profileData.userName);
    const profileDialog = ref(false);
    const userId = computed(() => store.state.userId);
    const profileImage = ref("/img/logo_company.svg");
    const fallbackProfileImage = "/img/logo_company.svg";
    const fallbackBrandImage = "/img/logo_company.svg";

    // Set profile image when userId is available
    watch(
      () => userId.value,
      (newUserId) => {
        if (newUserId) {
          profileImage.value = appOwnerImgSrcUrl + newUserId;
        }
      },
      { immediate: true },
    );

    const fetchAuth0Profile = async () => {
      const auth0 = getAuth0Client();
      try {
        const user = await auth0.getUser();
        if (user) {
          store.state.profileData.userEmail = user.email!;
          store.state.profileData.userName = user.name || user.nickname!;
        }
      } catch (error) {
        console.error("Failed to fetch Auth0 user:", error);
      }
    };

    watch(
      () => store.state.isAuthenticated,
      async (isAuthenticated) => {
        if (isAuthenticated) {
          await store.dispatch("setProfileInfo");
          await fetchAuth0Profile();
        }
      },
    );

    onMounted(async () => {
      if (store.state.isAuthenticated) {
        await store.dispatch("setProfileInfo");
        await fetchAuth0Profile();
      }
    });

    function dialogToggle() {
      profileDialog.value = !profileDialog.value;
    }

    async function logOut() {
      dialogToggle();
      try {
        await getAuthSessionManager().handleManualLogout();
      } catch {
        notifyError(false, MANUAL_LOGOUT_FAILURE_MESSAGE, "negative", "report_problem");
      }
    }

    return {
      brandImage,
      userEmail,
      userName,
      profileDialog,
      profileImage,
      fallbackProfileImage,
      fallbackBrandImage,
      dialogToggle,
      logOut,
      viewRbac,
    };
  },
});
